import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { contentService, Content } from '@services/content-service';
import { ApiError } from '@services/api/types';
import { setStatus } from '../app/appSlice';
import i18next from 'i18next';

interface ContentState {
  selectedContent: string | null;
  contents: Content[];
  isLoading: boolean;
  error: string | null;
  errorDetails: string | null;
}

const initialState: ContentState = {
  selectedContent: null,
  contents: [],
  isLoading: false,
  error: null,
  errorDetails: null,
};

export const createMultipleContents = createAsyncThunk<
  Content[],
  Omit<Content, 'status'>[],
  { rejectValue: ApiError }
>('content/createMultiple', async (contents, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setStatus({ type: 'loading', message: i18next.t('status.creating') }));
    const result = await contentService.createMultiple(contents);
    dispatch(setStatus({ type: 'success', message: i18next.t('status.created') }));
    return result;
  } catch (error) {
    const apiError = error as ApiError;
    // Extraer el mensaje y los detalles del error
    const errorMessage = apiError.error?.message || i18next.t('status.errorCreating');
    const errorDetails = apiError.error?.details?.specificDetails || apiError.error?.details || '';

    // Actualizar el estado con el mensaje de error y los detalles
    dispatch(
      setStatus({
        type: 'error',
        message: errorMessage,
        details: errorDetails as string,
      })
    );

    return rejectWithValue(apiError);
  }
});

// Thunk para obtener todos los contenidos
export const getAllContents = createAsyncThunk(
  'content/getAllContents',
  async (_, { rejectWithValue }) => {
    try {
      const response = await contentService.getAllContents();
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const clearAllContents = createAsyncThunk<void, void, { rejectValue: ApiError }>(
  'content/clearAll',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setStatus({ type: 'loading', message: i18next.t('status.clearing') }));
      const response = await contentService.clearAllContents();

      // Actualizar el estado con el mensaje de éxito y los detalles
      dispatch(
        setStatus({
          type: 'success',
          message: i18next.t('status.cleared'),
          details: i18next.t('status.deletedCount', { count: response.deletedCount }),
        })
      );

      // Refrescar la tabla después de eliminar todos los contenidos
      dispatch(getAllContents());

      return;
    } catch (error) {
      const apiError = error as ApiError;
      // Extraer el mensaje y los detalles del error
      const errorMessage = apiError.error?.message || i18next.t('status.errorClearing');
      const errorDetails =
        apiError.error?.details?.specificDetails || apiError.error?.details || '';

      // Actualizar el estado con el mensaje de error y los detalles
      dispatch(
        setStatus({
          type: 'error',
          message: errorMessage,
          details: errorDetails as string,
        })
      );

      return rejectWithValue(apiError);
    }
  }
);

export const unpublishContent = createAsyncThunk<Content, string, { rejectValue: ApiError }>(
  'content/unpublishContent',
  async (contentId, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setStatus({ type: 'loading', message: i18next.t('status.unpublishing') }));
      const response = await contentService.unpublishContent(contentId);
      dispatch(setStatus({ type: 'success', message: i18next.t('status.unpublished') }));
      return response;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message;
      const errorDetails = error.response?.data?.details || '';
      dispatch(
        setStatus({
          type: 'error',
          message: i18next.t('status.errorUnpublishing'),
          details: errorDetails || errorMessage,
        })
      );
      return rejectWithValue(error as ApiError);
    }
  }
);

export const deleteContent = createAsyncThunk<void, string, { rejectValue: ApiError }>(
  'content/deleteContent',
  async (contentId, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setStatus({ type: 'loading', message: i18next.t('status.deleting') }));
      await contentService.deleteContent(contentId);
      dispatch(setStatus({ type: 'success', message: i18next.t('status.deleted') }));
      dispatch(getAllContents());
      return;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message;
      const errorDetails = error.response?.data?.details || '';
      dispatch(
        setStatus({
          type: 'error',
          message: i18next.t('status.errorDeleting'),
          details: errorDetails || errorMessage,
        })
      );
      return rejectWithValue(error as ApiError);
    }
  }
);

export const contentSlice = createSlice({
  name: 'content',
  initialState,
  reducers: {
    setSelectedContent: (state, action: PayloadAction<string>) => {
      state.selectedContent = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (
      state,
      action: PayloadAction<{ message: string | null; details?: string | null }>
    ) => {
      state.error = action.payload.message;
      state.errorDetails = action.payload.details || null;
    },
    clearError: state => {
      state.error = null;
      state.errorDetails = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(createMultipleContents.pending, state => {
        state.isLoading = true;
        state.error = null;
        state.errorDetails = null;
      })
      .addCase(createMultipleContents.fulfilled, (state, action) => {
        state.isLoading = false;
        state.contents = action.payload;
        state.error = null;
        state.errorDetails = null;
      })
      .addCase(createMultipleContents.rejected, (state, action) => {
        state.isLoading = false;
        // Extraer el mensaje y los detalles del error
        const errorMessage =
          action.payload?.error?.message || i18next.t('newContent.errors.submitError');
        const errorDetails =
          action.payload?.error?.details?.specificDetails || action.payload?.error?.details || '';

        state.error = errorMessage;
        state.errorDetails = errorDetails as string;
      })
      // Casos para getAllContents
      .addCase(getAllContents.pending, state => {
        state.isLoading = true;
        state.error = null;
        state.errorDetails = null;
      })
      .addCase(getAllContents.fulfilled, (state, action) => {
        state.isLoading = false;
        state.contents = action.payload;
        state.error = null;
        state.errorDetails = null;
      })
      .addCase(getAllContents.rejected, (state, action) => {
        state.isLoading = false;
        const apiError = action.payload as ApiError;
        const errorMessage = apiError?.error?.message || i18next.t('status.errorLoading');
        const errorDetails =
          apiError?.error?.details?.specificDetails || apiError?.error?.details || '';

        state.error = errorMessage;
        state.errorDetails = errorDetails as string;
      })
      // Casos para clearAllContents
      .addCase(clearAllContents.pending, state => {
        state.isLoading = true;
        state.error = null;
        state.errorDetails = null;
      })
      .addCase(clearAllContents.fulfilled, state => {
        state.isLoading = false;
        state.contents = [];
        state.error = null;
        state.errorDetails = null;
      })
      .addCase(clearAllContents.rejected, (state, action) => {
        state.isLoading = false;
        const apiError = action.payload as ApiError;
        const errorMessage = apiError?.error?.message || i18next.t('status.errorClearing');
        const errorDetails =
          apiError?.error?.details?.specificDetails || apiError?.error?.details || '';

        state.error = errorMessage;
        state.errorDetails = errorDetails as string;
      });
  },
});

export const { setSelectedContent, setLoading, setError, clearError } = contentSlice.actions;

export default contentSlice.reducer;
